import _ from 'lodash'
import axios from 'axios'
import { notify } from '@/notifications'
import router from '@/router'
import ModalFactory from '@/components/Modals/ModalFactory'

const LOGIN = 'LOGIN'
const LOGOUT = 'LOGOUT'
const UPDATE_USER = 'UPDATE_USER'
const ACCEPT_TERMS = 'ACCEPT_TERMS'
const SAVE_ORGANIZATION = 'SAVE_ORGANIZATION'
const CLEAR_ORGANIZATION = 'CLEAR_ORGANIZATION'

const USER_SCHEMA = [
    'id',
    'name',
    'title',
    'username',
    'email',
    'confirmed',
    'accepted_terms',
    'email_verified_at',
    'last_login_at',
    'organization_id',
    'created_at',
    'updated_at',
    'can',
    'role_names',
    'gids',
    'organization',
    'refreshed_at',
    'support',
    'is_premium',
]

function userSchemaIsCorrect(user) {
    const userKeys = Object.keys(user).sort()
    const schemaKeys = USER_SCHEMA.sort()
    const matches = JSON.stringify(userKeys) === JSON.stringify(schemaKeys)
    if (!matches) {
        // console.error('User schema error!')
    }

    return matches
}

export default {
    state: {
        token: localStorage.getItem('token') || null,
        user: JSON.parse(localStorage.getItem('user')) || null,
        organization: JSON.parse(localStorage.getItem('organization')) || null,
        globalFeatureFlags: {},
    },

    getters: {
        auth(state) {
            if (state.token === null || state.user === null) {
                return false
            }

            return userSchemaIsCorrect(state.user)
        },

        user(state) {
            return state.user
        },

        userGid(state) {
            return state.user.gids[0]
        },

        organization(state) {
            return state.organization
        },

        hasRole(state) {
            return (roleName) => _.indexOf(state.user.role_names, roleName) !== -1
        },

        hasPermission(state) {
            return (permission) => state.user.can.indexOf(permission) !== -1
        },

        hasPermissions(state) {
            return (permissions) => _.every(permissions, (permission) => state.user.can.indexOf(permission) !== -1)
        },

        hasFeatureFlag(state) {
            return (flagName) => {
                if (state.organization?.feature_flags?.[flagName] !== undefined) {
                    return state.organization.feature_flags[flagName] === true
                }

                return state.globalFeatureFlags[flagName] === true
            }
        },

        shouldRefreshUser: (state) => () => {
            const now = Math.floor(Date.now() / 1000)
            return !state.user.refreshed_at || now - state.user.refreshed_at > 10 * 60
        },

        organizationHasFeature(state) {
            return (feature) => {
                return (
                    state.organization &&
                    state.organization.features.some((testFeature) => testFeature.feature === feature)
                )
            }
        },
    },

    mutations: {
        [LOGIN](state, { token }) {
            state.token = token
            localStorage.setItem('token', token)
            axios.defaults.headers.common.Authorization = `Bearer ${state.token}`
        },

        [LOGOUT](state) {
            state.token = null
            state.user = null
            state.organization = null
            localStorage.removeItem('token')
            localStorage.removeItem('user')
            localStorage.removeItem('organization')
            axios.defaults.headers.common.Authorization = ''
        },

        [UPDATE_USER](state, { user, organization, globalFeatureFlags }) {
            user.refreshed_at = Math.floor(Date.now() / 1000)
            state.user = user
            state.organization = organization
            state.globalFeatureFlags = globalFeatureFlags
            localStorage.setItem('organization', JSON.stringify(state.organization))
            localStorage.setItem('user', JSON.stringify(state.user))
        },

        [ACCEPT_TERMS](state) {
            state.user.accepted_terms = 1
            localStorage.setItem('user', JSON.stringify(state.user))
        },

        [SAVE_ORGANIZATION](state, organization) {
            state.organization = organization
            localStorage.setItem('organization', JSON.stringify(organization))
        },

        [CLEAR_ORGANIZATION](state) {
            state.organization = null
            localStorage.removeItem('organization')
        },
    },

    actions: {
        initializeStore({ commit, state, dispatch }) {
            axios.interceptors.response.use(
                (response) => response,
                (error) => {
                    const isLoginRroute =
                        router.currentRoute.value.name === 'login' || router.currentRoute.value.name === 'login-token'
                    if (error.response.status === 401 && !isLoginRroute) {
                        dispatch('forceLogout')
                        window.location.href = '/login'
                    } else if (error.response.status === 402) {
                        ModalFactory.paywallError(error.response.data.type, error.response.data.limit).then(
                            (upgrade) => {
                                if (upgrade) {
                                    dispatch('mission/setUpsellPageDisplay', true)
                                }
                            }
                        )
                    } else if (error.response.status === 403) {
                        notify('You are not authorized to perform this operation.')
                    }
                    throw error
                }
            )
            if (state.token) {
                axios.defaults.headers.common.Authorization = `Bearer ${state.token}`
            }
        },

        login({ commit, dispatch }, payload) {
            return new Promise((resolve, reject) => {
                axios
                    .post('auth/login', {
                        organization: payload.organization,
                        email: payload.email,
                        password: payload.password,
                        registration_token: payload.registration_token,
                    })
                    .then(({ data }) => {
                        commit(LOGIN, { token: data.token })
                        dispatch('updateUser', {
                            organization: data.organization,
                            user: data.user,
                            globalFeatureFlags: data.global_feature_flags,
                        })
                        resolve()
                    })
                    .catch((error) => {
                        reject(error)
                    })
            })
        },

        logout({ dispatch }) {
            return new Promise((resolve, reject) => {
                const redirectUrl = '/login'

                axios.post('auth/logout').finally(() => {
                    dispatch('forceLogout')
                    resolve(redirectUrl)
                })
            })
        },

        forceLogout({ commit, dispatch }) {
            commit(LOGOUT)
            commit('mission/CLEAR_MISSION')
            dispatch('identityUpdated')
        },

        verifyUser({ dispatch }) {
            return new Promise((resolve, reject) => {
                axios
                    .get('auth/verify-user')
                    .then(({ data }) => {
                        dispatch('updateUser', {
                            organization: data.organization,
                            user: data.user,
                            globalFeatureFlags: data.global_feature_flags,
                        })
                        resolve()
                    })
                    .catch((error) => {
                        reject(error)
                    })
            })
        },

        acceptTerms({ commit }) {
            return new Promise((resolve, reject) => {
                axios
                    .post('onboarding/accept-terms')
                    .then(() => {
                        commit(ACCEPT_TERMS)
                        resolve()
                    })
                    .catch((error) => {
                        reject(error)
                    })
            })
        },

        async getOrganization({ dispatch }, { slug }) {
            const response = await axios.get(`organizations/${slug}/info`)
            dispatch('saveOrganization', response.data)
        },

        clearOrganization({ commit, dispatch }) {
            commit(CLEAR_ORGANIZATION)
            dispatch('identityUpdated')
        },

        saveOrganization({ commit, dispatch, state }, organization) {
            commit(SAVE_ORGANIZATION, organization)
            dispatch('identityUpdated')
        },

        updateUser({ commit, dispatch }, { organization, user, globalFeatureFlags = {} }) {
            commit(UPDATE_USER, { organization, user, globalFeatureFlags })
            dispatch('identityUpdated')
        },

        identityUpdated() {},
    },
}
